import { dateKeyInMoldova } from "./catalog";
import {
  COMPARISON_GROUP_DEFINITIONS,
  comparisonVariant,
  type ComparisonVariant,
} from "./comparison-group-definitions";
import { normalizeSearch, queryVariants } from "./search-normalization";
import type { ProductView, UnitPriceBasis } from "./types";

export const COMPARISON_STARTER_SHELF_MAX_FAMILIES = 4;
export const COMPARISON_STARTER_SHELF_MIN_FAMILIES = 2;
export const COMPARISON_SEARCH_MAX_FAMILIES = 2;
// Four distinct offers in total, not a reference product plus four others.
export const COMPARISON_STARTER_SHELF_MIN_OFFERS = 4;
export const COMPARISON_STARTER_SHELF_MIN_STORES = 2;
export type ComparisonStarterFamilyId = string;

export interface ComparisonStarterFamily {
  id: ComparisonStarterFamilyId;
  comparisonGroupId: string;
  labelRu: string;
  labelRo: string;
  labelUk: string;
  variant: ComparisonVariant;
  tier: number;
  basis: UnitPriceBasis;
  products: ProductView[];
  storeCount: number;
  minimumUnitPrice: number;
}

const COMPARISON_SEARCH_STOP_WORDS = new Set([
  "and", "the", "with", "для", "или", "как", "при", "это", "din", "pentru",
  "prin", "sau", "care", "este", "si", "în", "in", "de", "la", "cu", "pe",
]);

export function normalizeComparisonText(value: string): string {
  return value.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("ro");
}

export function comparisonOfferKey(product: ProductView): string {
  return `${product.catalog_id}:${product.id}`;
}

/** A catalog advert is not an extra offer when the same store repeats its SKU. */
function skuKey(product: ProductView): string {
  return `${product.catalog.chain}:${product.product_id || normalizeComparisonText(
    `${product.product_ro || product.product_ru}|${product.brand ?? ""}|${product.weight_or_volume ?? ""}`,
  )}`;
}

function hasUnconditionalBasePrice(product: ProductView, text: string): boolean {
  if (product.required_card) return false;
  if (/(?:\d\s*[+×x]\s*\d|\bgratis\b|бесплат|pret\s*\/\s*set|цена\s*за\s*набор)/.test(text)) return false;
  if (/(?:cashback|кэшбэк|кешбек|loial|лояльн|cu card|cardul|по карт|карт[аеой]+\s+(?:клиента|магазина)|cupon|купон|voucher|doar online|только онлайн|doar in|только в|aplicat|приложении|цена от|(?:^|\s)(?:de la|от)\s*(?:;|$))/.test(text)) return false;
  // Multi-buy advertisements are safe here only when an explicit single-unit
  // tier matches the saved package price. Never compare the bulk tier as retail.
  if (/(?:de la\s*\d|от\s*\d|\d\s*[–-]\s*\d\s*(?:buc|штук)|la cumpar|при покуп)/.test(text)) {
    const single = text.match(/(?:^|[;\s.])1\s*(?:[–-]\s*\d+\s*)?(?:buc(?:ati|ata)?\.?|шт\.?|штук[аи]?)\s*:\s*(\d+(?:[.,]\d+)?)/);
    return !!single && Math.abs(Number(single[1]!.replace(",", ".")) - product.new_price_mdl!) < 0.005;
  }
  return true;
}

function validPrice(product: ProductView): boolean {
  const unit = product.unit_price;
  if (!unit || unit.is_range || !Number.isFinite(unit.minimum_mdl) ||
      !Number.isFinite(unit.maximum_mdl) || unit.minimum_mdl <= 0 ||
      unit.minimum_mdl !== unit.maximum_mdl || product.new_price_mdl === null ||
      !Number.isFinite(product.new_price_mdl) || product.new_price_mdl <= 0) return false;
  // A missing package is allowed only for an explicitly per-unit sale, e.g. kg.
  return !!product.weight_or_volume?.trim() || Math.abs(product.new_price_mdl - unit.minimum_mdl) < 0.005;
}

function compareOffers(left: ProductView, right: ProductView): number {
  return left.unit_price!.minimum_mdl - right.unit_price!.minimum_mdl ||
    left.new_price_mdl! - right.new_price_mdl! ||
    right.catalog.valid_until.localeCompare(left.catalog.valid_until) ||
    left.catalog.chain.localeCompare(right.catalog.chain) ||
    comparisonOfferKey(left).localeCompare(comparisonOfferKey(right));
}

/** All currently verifiable comparisons, not just a hard-coded starter list.
 * Never mutates products or removes anything from the main feed. */
export function buildComparisonCatalog(
  products: readonly ProductView[],
  today = dateKeyInMoldova(new Date()),
): ComparisonStarterFamily[] {
  const groups = new Map<string, ComparisonStarterFamily>();
  for (const product of products) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(product.catalog.valid_from) ||
        !/^\d{4}-\d{2}-\d{2}$/.test(product.catalog.valid_until) ||
        product.catalog.valid_from > today || product.catalog.valid_until < today ||
        product.category_id === "alcohol" || !validPrice(product)) continue;
    const original = COMPARISON_GROUP_DEFINITIONS.get(product.comparison_group_id ?? "");
    if (!original) continue;
    const title = normalizeComparisonText(`${product.product_ro}\n${product.product_ru}`);
    const notes = normalizeComparisonText(`${product.note_ro ?? ""}\n${product.note_ru ?? ""}`);
    const text = `${title}\n${notes}`;
    if (!original.include.test(title) || original.exclude?.test(text) ||
        !hasUnconditionalBasePrice(product, text)) continue;
    const variant = comparisonVariant(original.id, text, title);
    if (!variant) continue;
    // Some older dictionaries use a generic water group. Merge the confirmed
    // subtype with its newer taxonomy ID so it does not become a duplicate tile.
    const groupId = original.id === "drinks_water" ? `drinks_water_${variant.id}` : original.id;
    const definition = COMPARISON_GROUP_DEFINITIONS.get(groupId) ?? original;
    const basis = product.unit_price!.basis;
    const id = `${groupId}:${variant.id}:${basis}`;
    let group = groups.get(id);
    if (!group) {
      group = {
        id, comparisonGroupId: groupId, labelRu: definition.labelRu, labelRo: definition.labelRo,
        labelUk: definition.labelUk,
        variant, tier: definition.tier, basis, products: [], storeCount: 0, minimumUnitPrice: 0,
      };
      groups.set(id, group);
    }
    group.products.push(product);
  }
  return [...groups.values()].flatMap((group) => {
    const seen = new Set<string>();
    group.products.sort(compareOffers);
    group.products = group.products.filter((product) => {
      const key = skuKey(product);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    group.storeCount = new Set(group.products.map((product) => product.catalog.chain)).size;
    if (group.products.length < COMPARISON_STARTER_SHELF_MIN_OFFERS ||
        group.storeCount < COMPARISON_STARTER_SHELF_MIN_STORES) return [];
    group.minimumUnitPrice = group.products[0]!.unit_price!.minimum_mdl;
    return [group];
  }).sort((left, right) => left.tier - right.tier || left.id.localeCompare(right.id));
}

/** Compact preview: the cheapest offer and the cheapest at a different store.
 * The expanded comparison retains every eligible SKU, including same-store ones. */
export function comparisonPreviewProducts(family: ComparisonStarterFamily): ProductView[] {
  const stores = new Set<string>();
  return family.products.filter((product) => {
    if (stores.has(product.catalog.chain)) return false;
    stores.add(product.catalog.chain);
    return true;
  }).slice(0, 2);
}

export function comparisonUnitDifference(product: ProductView, family: ComparisonStarterFamily): number {
  return Math.max(0, Math.round((product.unit_price!.minimum_mdl - family.minimumUnitPrice) * 100) / 100);
}

const COMPARISON_EDITORIAL_PRIORITY = new Map(
  [...COMPARISON_GROUP_DEFINITIONS.keys()].map((id, index) => [id, index]),
);

function compareComparisonPriority(left: ComparisonStarterFamily, right: ComparisonStarterFamily): number {
  return left.tier - right.tier ||
    right.storeCount - left.storeCount ||
    Math.min(right.products.length, 12) - Math.min(left.products.length, 12) ||
    (COMPARISON_EDITORIAL_PRIORITY.get(left.comparisonGroupId) ?? Number.MAX_SAFE_INTEGER) -
      (COMPARISON_EDITORIAL_PRIORITY.get(right.comparisonGroupId) ?? Number.MAX_SAFE_INTEGER) ||
    left.id.localeCompare(right.id);
}

/** Stable utility order: reviewed everyday-need tier first, then breadth of
 * store coverage and a capped offer count. Exact ties use the reviewed
 * taxonomy order instead of an arbitrary string sort. A second variant of the
 * same parent family waits until the other families in its tier get a turn. */
export function orderComparisonFamilies(
  families: readonly ComparisonStarterFamily[],
): ComparisonStarterFamily[] {
  const ranked = [...families].sort(compareComparisonPriority);
  const ordered: ComparisonStarterFamily[] = [];
  for (const tier of [...new Set(ranked.map((family) => family.tier))]) {
    const remaining = ranked.filter((family) => family.tier === tier);
    while (remaining.length) {
      const seen = new Set<string>();
      for (let index = 0; index < remaining.length;) {
        const family = remaining[index]!;
        if (seen.has(family.comparisonGroupId)) {
          index += 1;
          continue;
        }
        seen.add(family.comparisonGroupId);
        ordered.push(family);
        remaining.splice(index, 1);
      }
    }
  }
  return ordered;
}

function comparisonSearchTokens(value: string): string[] {
  return normalizeSearch(value).split(" ").filter((token) =>
    !COMPARISON_SEARCH_STOP_WORDS.has(token) &&
    (/^\d+(?:\.\d+)?$/u.test(token) || token.length >= 3),
  );
}

function editDistanceAtMostOne(left: string, right: string): boolean {
  if (left === right) return true;
  if (Math.abs(left.length - right.length) > 1) return false;
  let leftIndex = 0;
  let rightIndex = 0;
  let edits = 0;
  while (leftIndex < left.length && rightIndex < right.length) {
    if (left[leftIndex] === right[rightIndex]) {
      leftIndex += 1;
      rightIndex += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) return false;
    if (left.length > right.length) leftIndex += 1;
    else if (right.length > left.length) rightIndex += 1;
    else {
      leftIndex += 1;
      rightIndex += 1;
    }
  }
  return edits + Number(leftIndex < left.length || rightIndex < right.length) <= 1;
}

function comparisonSearchTokenScore(query: string, target: string): number {
  if (query === target) return 6;
  if (/^\d/u.test(query) || /^\d/u.test(target)) return 0;
  const shortest = Math.min(query.length, target.length);
  if (shortest >= 3 && (query.startsWith(target) || target.startsWith(query))) return 5;
  let common = 0;
  while (common < shortest && query[common] === target[common]) common += 1;
  if (shortest >= 4 && common >= Math.min(5, shortest - 1) && Math.abs(query.length - target.length) <= 2) return 4;
  return shortest >= 5 && editDistanceAtMostOne(query, target) ? 3 : 0;
}

function comparisonFamilySearchScore(family: ComparisonStarterFamily, query: string): number {
  const queryNormalized = normalizeSearch(query);
  const queryTokens = comparisonSearchTokens(queryNormalized);
  if (!queryTokens.length) return 0;
  let best = 0;
  for (const label of [family.labelRu, family.labelRo, family.labelUk]) {
    const labelNormalized = normalizeSearch(label);
    const labelTokens = comparisonSearchTokens(labelNormalized);
    if (!labelTokens.length) continue;
    const tokenScores = labelTokens.map((target) =>
      Math.max(0, ...queryTokens.map((candidate) => comparisonSearchTokenScore(candidate, target))),
    );
    const matched = tokenScores.filter((score) => score > 0).length;
    if (!matched) continue;
    let score = 3_000 + matched * 500 + tokenScores.reduce((sum, value) => sum + value, 0) * 20;
    if (queryNormalized === labelNormalized) score += 5_000;
    else if (queryNormalized.includes(labelNormalized) || labelNormalized.includes(queryNormalized)) score += 1_000;
    const variantTokens = comparisonSearchTokens(`${family.variant.ru} ${family.variant.ro} ${family.variant.uk}`);
    score += variantTokens.reduce((sum, target) =>
      sum + Math.max(0, ...queryTokens.map((candidate) => comparisonSearchTokenScore(candidate, target))) * 15,
    0);
    best = Math.max(best, score);
  }
  return best;
}

/** Returns only high-confidence family-name matches. Product brands and raw
 * descriptions do not activate the comparison module on their own. Query
 * variants cover RU/RO diacritics, keyboard layout and transliteration. */
export function comparisonFamiliesForSearch(
  families: readonly ComparisonStarterFamily[],
  query: string,
  limit = COMPARISON_SEARCH_MAX_FAMILIES,
): ComparisonStarterFamily[] {
  if (limit <= 0 || !normalizeSearch(query)) return [];
  const stable = orderComparisonFamilies(families);
  const priority = new Map(stable.map((family, index) => [family.id, index]));
  const scores = new Map<string, number>();
  for (const variant of queryVariants(query)) {
    for (const family of stable) {
      const score = comparisonFamilySearchScore(family, variant.value) * variant.weight;
      if (score > (scores.get(family.id) ?? 0)) scores.set(family.id, score);
    }
  }
  const candidates = stable.filter((family) => (scores.get(family.id) ?? 0) > 0)
    .sort((left, right) =>
      (scores.get(right.id) ?? 0) - (scores.get(left.id) ?? 0) ||
      (priority.get(left.id) ?? 0) - (priority.get(right.id) ?? 0),
    );
  const result: ComparisonStarterFamily[] = [];
  const parents = new Set<string>();
  for (const family of candidates) {
    if (parents.has(family.comparisonGroupId)) continue;
    parents.add(family.comparisonGroupId);
    result.push(family);
    if (result.length >= limit) break;
  }
  return result;
}

/** Keeps high-confidence search matches first without dropping or duplicating
 * any family from the already reviewed full comparison order. */
export function comparisonFamiliesWithMatchesFirst(
  families: readonly ComparisonStarterFamily[],
  matches: readonly ComparisonStarterFamily[],
): ComparisonStarterFamily[] {
  const available = new Map(families.map((family) => [family.id, family]));
  const seen = new Set<string>();
  const ordered: ComparisonStarterFamily[] = [];
  for (const family of [...matches, ...families]) {
    const canonical = available.get(family.id);
    if (!canonical || seen.has(canonical.id)) continue;
    seen.add(canonical.id);
    ordered.push(canonical);
  }
  return ordered;
}

// Kept for the existing offline feed audit. The interactive UI orders the full
// catalog through its visit controller before taking this compact prefix.
export function buildComparisonStarterShelf(products: readonly ProductView[], today?: string): ComparisonStarterFamily[] {
  const all = orderComparisonFamilies(buildComparisonCatalog(products, today));
  return all.length < COMPARISON_STARTER_SHELF_MIN_FAMILIES ? [] :
    all.slice(0, all.length >= COMPARISON_STARTER_SHELF_MAX_FAMILIES ? 4 : 2);
}

export function comparisonStarterFamilyProducts(products: readonly ProductView[], familyId: string, today?: string): ProductView[] {
  return buildComparisonCatalog(products, today).find((family) => family.id === familyId)?.products ?? [];
}

export function comparisonStarterShelfEnabledForSurface(options: { website: boolean }): boolean {
  return !options.website;
}
